import * as Sentry from '@sentry/react'

interface TelemetryRuntimeConfig {
  dsn?: string
  environment: string
  release?: string
}

const SENSITIVE_KEY_PATTERN =
  /password|passwd|secret|token|authorization|cookie|api[-_]?key|access[-_]?key|refresh|jwt|bearer|session[-_]?id|fic[-_]?|boldsign|codice[-_]?fiscale|partita[-_]?iva/i

const REDACTED = '[redacted]'

function readRuntimeConfig(): TelemetryRuntimeConfig {
  const runtime = (window as { CONFIG?: Record<string, string | undefined> }).CONFIG ?? {}
  return {
    dsn:
      runtime.VITE_GLITCHTIP_DSN ||
      (import.meta.env.VITE_GLITCHTIP_DSN as string | undefined) ||
      undefined,
    environment:
      runtime.VITE_ENVIRONMENT ||
      (import.meta.env.VITE_ENVIRONMENT as string | undefined) ||
      'development',
    release:
      runtime.VITE_APP_VERSION ||
      (import.meta.env.VITE_APP_VERSION as string | undefined) ||
      undefined,
  }
}

export function sanitizeValue(value: unknown, key = '', depth = 0): unknown {
  if (SENSITIVE_KEY_PATTERN.test(key)) return REDACTED
  if (depth > 6 || value == null) return value
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, '', depth + 1))
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = sanitizeValue(v, k, depth + 1)
    }
    return out
  }
  if (typeof value === 'string' && SENSITIVE_KEY_PATTERN.test(value)) return value
  return value
}

function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin)
    for (const param of [...parsed.searchParams.keys()]) {
      if (SENSITIVE_KEY_PATTERN.test(param)) parsed.searchParams.set(param, REDACTED)
    }
    return parsed.toString()
  } catch {
    return url
  }
}

export function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.request) {
    if (event.request.url) event.request.url = sanitizeUrl(event.request.url)
    if (event.request.headers) {
      event.request.headers = sanitizeValue(event.request.headers) as Record<string, string>
    }
    if (event.request.cookies) event.request.cookies = sanitizeValue(event.request.cookies) as Record<string, string>
    if (event.request.data) event.request.data = sanitizeValue(event.request.data)
    if (event.request.query_string) event.request.query_string = sanitizeValue(event.request.query_string) as typeof event.request.query_string
  }
  if (event.user) {
    event.user = { id: event.user.id }
  }
  if (event.extra) event.extra = sanitizeValue(event.extra) as Record<string, unknown>
  if (event.contexts) event.contexts = sanitizeValue(event.contexts) as typeof event.contexts
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => ({
      ...crumb,
      data: crumb.data ? (sanitizeValue(crumb.data) as Record<string, unknown>) : crumb.data,
    }))
  }
  return event
}

let initialized = false

export function initTelemetry(): boolean {
  if (initialized) return true
  const config = readRuntimeConfig()
  if (!config.dsn) return false

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend: (event) => scrubEvent(event),
  })
  initialized = true
  return true
}

export function captureTelemetrySmokeTest(): string | undefined {
  if (!initTelemetry()) return undefined
  return Sentry.captureMessage('offertify-frontend-smoke-test', {
    level: 'info',
    tags: { smoke_test: 'true', source: 'manual-verification' },
    extra: { note: 'Evento sintetico di verifica ingestione GlitchTip, nessun dato reale.' },
  })
}

if (typeof window !== 'undefined') {
  ;(window as { offertifyTelemetrySmokeTest?: typeof captureTelemetrySmokeTest }).offertifyTelemetrySmokeTest =
    captureTelemetrySmokeTest
}
